export function fmt_num(n) {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e7) return `${(v / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${(v / 1e5).toFixed(2)}L`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return v.toFixed(0);
}

export function fmt_inr(n, signed = false) {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return "—";
  const prefix = signed && v > 0 ? "+" : "";
  return `${prefix}₹${fmt_num(v)}`;
}

export function fmt_inr_full(n, signed = false) {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return "—";
  const prefix = signed && v > 0 ? "+" : "";
  return `${prefix}₹ ${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export function fmt_pct(n, signed = true) {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return "—";
  const sign = signed && v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}

export function pct_class(n) {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return "";
  return v >= 0 ? "pos" : "neg";
}

/** Strip spaced hyphens/dashes in portfolio summary copy (e.g. "IT - Software"). */
export function format_portfolio_summary(text) {
  if (!text) return "";
  return String(text)
    .replace(/\s+[-–—]\s+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Compact token for symbol/code matching (ignores spaces, .NS/.BO suffixes). */
export function normalize_search_token(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\.(ns|bo)$/i, "")
    .replace(/\s+/g, "");
}

/** Spaced text for company-name matching. */
export function normalize_search_query(query) {
  return String(query || "")
    .trim()
    .toLowerCase()
    .replace(/\.(ns|bo)$/i, "")
    .replace(/\s{2,}/g, " ");
}

function is_numeric_exchange_code(code) {
  const compact = normalize_search_token(code).replace(/^0+/, "") || "0";
  return /^\d+$/.test(compact);
}

/** Resolve NSE/BSE codes from API fields or primary holding code. */
/** Normalize NSE/BSE ticker for peer exclusion (matches backend _normalize_ticker_code). */
export function portfolio_ticker_token(value) {
  let text = String(value || "").trim();
  if (!text) return "";
  text = text.toUpperCase().replace(/\.(NS|BO)$/i, "");
  if (/^\d/.test(text) && text.includes(".")) {
    text = text.split(".")[0];
  }
  return text.replace(/\s+/g, "");
}

/** All NSE/BSE/primary codes for a portfolio stock (peer exclusion). */
export function portfolio_exclude_codes(stocks) {
  const codes = new Set();
  for (const stock of stocks || []) {
    for (const field of [stock?.code, stock?.nse_code, stock?.bse_code]) {
      const token = portfolio_ticker_token(field);
      if (token) codes.add(token);
    }
  }
  return [...codes];
}

/** Drop industry peers that are already in the uploaded portfolio (browser-side filter). */
export function filter_industry_peers_outside_portfolio(top_peers, portfolio_stocks) {
  const own_codes = new Set(portfolio_exclude_codes(portfolio_stocks));
  if (!own_codes.size) return top_peers || [];
  return (top_peers || []).filter((peer) => {
    const token = portfolio_ticker_token(peer?.code);
    return token && !own_codes.has(token);
  });
}

export function get_stock_exchange_codes(stock) {
  const nse = stock?.nse_code ? String(stock.nse_code).trim() : null;
  const bse = stock?.bse_code ? String(stock.bse_code).trim() : null;
  if (nse || bse) {
    return { nse_code: nse, bse_code: bse };
  }

  const code = String(stock?.code || "").trim();
  if (!code) {
    return { nse_code: null, bse_code: null };
  }

  if (is_numeric_exchange_code(code)) {
    return { nse_code: null, bse_code: code.replace(/\.0+$/, "").split(".")[0] };
  }

  return { nse_code: code, bse_code: null };
}

/** Match portfolio stock by name, NSE ticker, or BSE numeric code. */
export const PERIOD_VS_NIFTY_ORDER = ["1m", "6m", "1y", "3y"];

export function get_period_vs_nifty_series(period_vs_nifty) {
  if (!period_vs_nifty || typeof period_vs_nifty !== "object") {
    return [];
  }

  return PERIOD_VS_NIFTY_ORDER.filter((period) => period_vs_nifty[period]).map((period) => ({
    period,
    stock: parseFloat(period_vs_nifty[period].stock),
    nifty: parseFloat(period_vs_nifty[period].nifty),
  }));
}

/** Weighted average of per-holding period vs Nifty for portfolio overview chart. */
export function build_portfolio_period_vs_nifty(stocks) {
  const buckets = {};

  for (const stock of stocks || []) {
    const period_vs_nifty = stock?.holding?.period_vs_nifty;
    if (!period_vs_nifty || typeof period_vs_nifty !== "object") continue;

    const holding = stock.holding || {};
    const weight =
      parseFloat(holding.net_market_value) ||
      parseFloat(holding.current_value) ||
      parseFloat(holding.net_invested) ||
      1;

    if (!weight || Number.isNaN(weight)) continue;

    for (const period of PERIOD_VS_NIFTY_ORDER) {
      const row = period_vs_nifty[period];
      if (!row) continue;

      const stock_ret = parseFloat(row.stock);
      const nifty_ret = parseFloat(row.nifty);
      if (Number.isNaN(stock_ret) || Number.isNaN(nifty_ret)) continue;

      if (!buckets[period]) {
        buckets[period] = { stock_w: 0, nifty_w: 0, weight: 0 };
      }
      buckets[period].stock_w += stock_ret * weight;
      buckets[period].nifty_w += nifty_ret * weight;
      buckets[period].weight += weight;
    }
  }

  const out = {};
  for (const period of PERIOD_VS_NIFTY_ORDER) {
    const bucket = buckets[period];
    if (bucket && bucket.weight > 0) {
      out[period] = {
        stock: bucket.stock_w / bucket.weight,
        nifty: bucket.nifty_w / bucket.weight,
      };
    }
  }

  return Object.keys(out).length ? out : null;
}

export function stock_matches_search(stock, query) {
  const q_compact = normalize_search_token(query);
  const q_spaced = normalize_search_query(query);
  if (!q_compact && !q_spaced) return true;

  const { nse_code, bse_code } = get_stock_exchange_codes(stock);
  const fields = [stock?.name, stock?.code, nse_code, bse_code, stock?.isin];

  for (const field of fields) {
    const raw = String(field || "").trim();
    if (!raw) continue;

    const compact = normalize_search_token(raw);
    const spaced = normalize_search_query(raw);

    if (q_compact && (compact.includes(q_compact) || compact === q_compact)) {
      return true;
    }
    if (q_spaced && spaced.includes(q_spaced)) {
      return true;
    }
  }

  return false;
}

/** Raw API action string (trimmed). */
export function normalize_stock_action(stock) {
  return String(stock?.action || "").trim();
}

/** Parsed holding return %, or null when unavailable. */
export function stock_return_pct(stock) {
  const value = parseFloat(stock?.holding?.return_pct);
  return Number.isNaN(value) ? null : value;
}

export function is_profit_position(stock) {
  const ret = stock_return_pct(stock);
  return ret != null && ret > 0;
}

/** Book profit bucket: profitable Reduce/Exit, or API profit_booking / sell_profit flags. */
export function is_book_profit_stock(stock) {
  const action = normalize_stock_action(stock).toLowerCase().replace(/_/g, " ");
  if (action === "reduce" || action === "exit") {
    return is_profit_position(stock);
  }
  const af = stock?.action_financials || {};
  return af.type === "sell_profit" || !!stock?.quantity_advice?.profit_booking;
}

/** Display label for action badges (matches portfolio_analyzer_v2.html badgeLabel). */
export function action_badge_label(stock) {
  const action = normalize_stock_action(stock);
  const qa = stock?.quantity_advice;
  const pct = qa?.pct_of_holding;

  if (action === "Accumulate") return "BUY MORE";
  if (action === "Average Down" || action === "average_down") return "Average Down";
  if (action === "Exit") {
    if (is_book_profit_stock(stock)) return "Book Profit";
    return pct != null && parseFloat(pct) >= 100 ? "Exit all" : "Exit";
  }
  if (action === "Reduce") {
    if (is_book_profit_stock(stock)) return "Book Profit";
    return "Cut Loss";
  }
  return action || "—";
}

/** Buy more = sidebar Buy More bucket (action Accumulate; not Hold / Reduce / Exit). */
export function is_buy_more_stock(stock) {
  const holding = stock?.holding || {};
  if (!holding.available) return false;
  const action = normalize_stock_action(stock);
  if (action !== "Accumulate") return false;

  const qty_adv = stock?.quantity_advice;
  if (qty_adv) {
    const direction = (qty_adv.direction || "").toLowerCase();
    if (direction === "sell" || direction === "reduce") return false;
    if (direction === "add") return true;
  }

  const af_type = (stock?.action_financials?.type || "").toLowerCase();
  if (af_type === "buy") return true;

  return true;
}

export function buy_more_stocks(stocks) {
  return (stocks || []).filter(is_buy_more_stock);
}

/** Sidebar route for a stock (Buy More, Cut Loss, Book Profit, Portfolio, etc.). */
export function nav_id_for_stock(stock) {
  const filter_key = stock_filter_key(stock);
  const nav_item = SIDEBAR_NAV.find(
    (item) => item.nav_type === "filter" && item.filter_id === filter_key
  );
  return nav_item?.id || "all";
}

export function nav_label_for_stock(stock) {
  const nav_id = nav_id_for_stock(stock);
  const item = SIDEBAR_NAV.find((n) => n.id === nav_id);
  return item?.label || "Portfolio";
}

/** Whether a stock belongs on the active action page (looser than list filters). */
export function stock_visible_in_filter(stock, filter_id) {
  if (!stock || !filter_id || filter_id === "all" || filter_id === "dashboard") {
    return true;
  }
  if (filter_id === "profit_booking") {
    return stock_filter_key(stock) === "profit_booking";
  }
  if (filter_id === "accumulate") {
    return stock_filter_key(stock) === "accumulate";
  }
  return stock_filter_key(stock) === filter_id;
}

export function stock_filter_key(stock) {
  const action = normalize_stock_action(stock).toLowerCase().replace(/_/g, " ");
  if (action === "accumulate") return "accumulate";
  if (action === "average down") return "average_down";
  if (action === "hold") return "hold";
  if (action === "exit") {
    return is_profit_position(stock) ? "profit_booking" : "exit";
  }
  if (action === "reduce") {
    return is_profit_position(stock) ? "profit_booking" : "cut_loss";
  }
  return "other";
}

export function action_badge_class(action) {
  const a = (action || "").toLowerCase();
  if (a === "accumulate" || a === "average down" || a === "average_down") return "badge_acc";
  if (a === "hold") return "badge_hold";
  if (a === "exit") return "badge_exit";
  if (a === "reduce") return "badge_trim";
  return "badge_muted";
}

/** Sidebar — same items & order as design mock */
export const SIDEBAR_NAV = [
  {
    id: "dashboard",
    label: "Dashboard",
    section: "overview",
    icon: "dashboard",
    nav_type: "filter",
    filter_id: "dashboard",
  },
  {
    id: "all",
    label: "Portfolio",
    section: "overview",
    icon: "portfolio",
    nav_type: "filter",
    filter_id: "all",
    show_count: true,
  },
  {
    id: "accumulate",
    label: "Buy More",
    section: "actions",
    icon: "buy",
    nav_type: "filter",
    filter_id: "accumulate",
    badge_variant: "success",
    show_badge: true,
    overview_priority: 4,
  },
  {
    id: "average_down",
    label: "Average Down",
    section: "actions",
    icon: "avg_down",
    nav_type: "filter",
    filter_id: "average_down",
    badge_variant: "success",
    show_badge: true,
    overview_priority: 3,
  },
  {
    id: "cut_loss",
    label: "Cut Loss",
    section: "actions",
    icon: "cut_loss",
    nav_type: "filter",
    filter_id: "cut_loss",
    badge_variant: "danger",
    show_badge: true,
    overview_priority: 1,
  },
  {
    id: "exit",
    label: "Exit",
    section: "actions",
    icon: "exit",
    nav_type: "filter",
    filter_id: "exit",
    badge_variant: "danger",
    show_badge: true,
    overview_priority: 0,
  },
  {
    id: "hold",
    label: "Hold",
    section: "actions",
    icon: "hold",
    nav_type: "filter",
    filter_id: "hold",
    badge_variant: "muted",
    show_badge: true,
    overview_priority: 6,
  },
  {
    id: "profit_booking",
    label: "Book Profit",
    section: "actions",
    icon: "book_profit",
    nav_type: "filter",
    filter_id: "profit_booking",
    badge_variant: "muted",
    show_badge: true,
    overview_priority: 2,
  },
  {
    id: "execution_plan",
    label: "Execution Plan",
    section: "tools",
    icon: "execution",
    nav_type: "tool",
    tool_action: "execution_plan",
  },
  {
    id: "reports",
    label: "Reports",
    section: "tools",
    icon: "reports",
    nav_type: "tool",
    tool_action: "reports",
  },
];

export function count_by_filter(stocks) {
  const counts = { all: stocks?.length || 0, dashboard: stocks?.length || 0 };
  (stocks || []).forEach((s) => {
    const k = stock_filter_key(s);
    counts[k] = (counts[k] || 0) + 1;
  });
  counts.accumulate = buy_more_stocks(stocks).length;
  counts.profit_booking = filter_stocks(stocks, "profit_booking").length;
  return counts;
}

/**
 * Risk speedometer: ((high_risk - low_risk) / high_risk) × 100.
 * low_risk = Buy More; high_risk = Average Down + Cut Loss + Book Profit + Exit.
 */
export function compute_dashboard_risk_meter_score(filter_counts) {
  const fc = filter_counts || {};
  const low_risk = fc.accumulate ?? 0;
  const high_risk =
    (fc.average_down || 0) +
    (fc.cut_loss || 0) +
    (fc.profit_booking || 0) +
    (fc.exit || 0);

  if (high_risk <= 0) {
    return low_risk > 0 ? 10 : null;
  }

  const raw = ((high_risk - low_risk) / high_risk) * 100;
  const rounded = Math.round(Math.max(0, Math.min(100, raw)));
  return Math.max(10, Math.min(90, rounded));
}

/** 0–39 low, 40–69 moderate, 70–100 high (matches inverted gauge bands). */
export function risk_meter_band_label(score) {
  if (score == null || Number.isNaN(score)) return "—";
  if (score < 40) return "Low risk";
  if (score < 70) return "Moderate risk";
  return "High risk";
}

export function risk_meter_band_key(score) {
  if (score == null || Number.isNaN(score)) return null;
  if (score < 40) return "low";
  if (score < 70) return "moderate";
  return "high";
}

/** Badge pill tone for Risk panel (low / moderate / high). */
export function risk_meter_badge_tone(score) {
  return risk_meter_band_key(score);
}

/** One-line Risk panel copy per meter band (replaces portfolio_risk_assessment text). */
export function risk_meter_summary_text(score, filter_counts) {
  const band = risk_meter_band_key(score);
  const fc = filter_counts || {};
  const low = fc.accumulate ?? 0;
  const hold = fc.hold ?? 0;
  const high =
    (fc.average_down || 0) +
    (fc.cut_loss || 0) +
    (fc.profit_booking || 0) +
    (fc.exit || 0);

  if (band === "low") {
    return [
      `Lower risk portfolio — ${low} Buy More name${low === 1 ? "" : "s"} lead the action mix.`,
      `Only ${high} defensive action${high === 1 ? "" : "s"} (trim, exit, avg down, book profit) are flagged.`,
      "Favour measured adds; defensive sells are not the main story right now.",
    ].join(" ");
  }
  if (band === "moderate") {
    return [
      "Moderate risk portfolio — some positions need attention.",
      `${hold} Hold name${hold === 1 ? "" : "s"} sit with ${high} defensive action${high === 1 ? "" : "s"} across the book.`,
      "Balance monitoring with selective trims or profit booking before adding new capital.",
    ].join(" ");
  }
  if (band === "high") {
    return [
      `High risk portfolio — ${high} defensive action${high === 1 ? "" : "s"} vs ${low} Buy More.`,
      "Cuts, exits, average-down, and profit booking dominate near-term work.",
      "Follow the execution plan closely and size risk down until the book stabilises.",
    ].join(" ");
  }
  return "Upload a portfolio to see action-mix risk.";
}

/** Dashboard Risk panel chips: action counts by risk bucket. */
export function build_dashboard_risk_chips(filter_counts) {
  const fc = filter_counts || {};
  const high_risk_count =
    (fc.average_down || 0) +
    (fc.cut_loss || 0) +
    (fc.profit_booking || 0) +
    (fc.exit || 0);

  return [
    { label: "Low risk", value: fc.accumulate ?? 0, tone: "pos" },
    { label: "Moderate risk", value: fc.hold ?? 0, tone: "muted" },
    { label: "High risk", value: high_risk_count, tone: "high_risk" },
  ];
}

export function filter_stocks(stocks, filter_id) {
  if (!filter_id || filter_id === "all" || filter_id === "dashboard") {
    return stocks || [];
  }
  if (filter_id === "accumulate") {
    return buy_more_stocks(stocks);
  }
  if (filter_id === "profit_booking") {
    return (stocks || []).filter((s) => stock_filter_key(s) === "profit_booking");
  }
  return (stocks || []).filter((s) => stock_filter_key(s) === filter_id);
}

const TOP_ACTION_OTHER_PRIORITY = 1_000;

/** Overview table urgency — from SIDEBAR_NAV.overview_priority (not sidebar display order). */
export function top_action_bucket_priority(bucket) {
  if (bucket === "other") return TOP_ACTION_OTHER_PRIORITY;
  if (bucket === "average_up") {
    const buy_more = SIDEBAR_NAV.find((n) => n.filter_id === "accumulate");
    return (buy_more?.overview_priority ?? 4) + 0.5;
  }
  const nav_item = SIDEBAR_NAV.find((n) => n.nav_type === "filter" && n.filter_id === bucket);
  return nav_item?.overview_priority ?? TOP_ACTION_OTHER_PRIORITY - 1;
}

/** Bucket for dashboard "Top Stock Actions" (aligns with filter-bar semantics). */
export function stock_action_bucket(stock) {
  const key = stock_filter_key(stock);
  if (key === "accumulate") return "accumulate";
  const nav_item = SIDEBAR_NAV.find((n) => n.nav_type === "filter" && n.filter_id === key);
  return nav_item ? key : "other";
}

/** Actionable holdings for overview table, sorted by urgency (limit <= 0 = show all). */
export function select_top_action_stocks(stocks, limit = 0) {
  const cap = limit == null || Number(limit) <= 0 ? null : Math.max(1, Number(limit));
  const ranked = (stocks || [])
    .filter((stock) => stock?.holding?.available && stock?.code)
    .map((stock) => {
      const bucket = stock_action_bucket(stock);
      const priority = top_action_bucket_priority(bucket);
      const weight = parseFloat(stock?.portfolio_impact?.weight_pct);
      const abs_return = Math.abs(parseFloat(stock?.holding?.return_pct));
      return {
        stock,
        priority,
        weight: Number.isNaN(weight) ? 0 : weight,
        abs_return: Number.isNaN(abs_return) ? 0 : abs_return,
      };
    })
    .filter((row) => row.priority < TOP_ACTION_OTHER_PRIORITY)
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      if (b.weight !== a.weight) return b.weight - a.weight;
      return b.abs_return - a.abs_return;
    });

  const picked = cap == null ? ranked.map((row) => row.stock) : ranked.slice(0, cap).map((row) => row.stock);
  if (cap == null || picked.length >= cap) return picked;

  const seen = new Set(picked.map((s) => s.code));
  const fill = (stocks || [])
    .filter((stock) => stock?.holding?.available && stock?.code && !seen.has(stock.code))
    .sort((a, b) => {
      const wa = parseFloat(a?.portfolio_impact?.weight_pct) || 0;
      const wb = parseFloat(b?.portfolio_impact?.weight_pct) || 0;
      return wb - wa;
    })
    .slice(0, cap - picked.length);

  return [...picked, ...fill];
}

/** Per-page UI: each sidebar route shows only its own main content. */
export function get_nav_page_config(nav_id) {
  const item = SIDEBAR_NAV.find((n) => n.id === nav_id);
  if (!item || nav_id === "dashboard") {
    return {
      page_mode: "dashboard",
      title: "Portfolio Overview",
      show_search: false,
      show_kpis: false,
      filter_id: "dashboard",
    };
  }
  if (item.nav_type === "tool") {
    if (item.tool_action === "execution_plan") {
      return {
        page_mode: "execution",
        title: item.label,
        show_search: false,
        show_kpis: false,
        filter_id: null,
      };
    }
    if (item.tool_action === "reports") {
      return {
        page_mode: "reports",
        title: item.label,
        show_search: false,
        show_kpis: false,
        filter_id: null,
      };
    }
  }
  return {
    page_mode: "stock",
    title: item.label,
    show_search: true,
    show_kpis: false,
    filter_id: item.filter_id || "all",
  };
}

export function get_nav_item_count(item, counts) {
  if (item.show_count || item.show_badge) {
    if (item.filter_id === "profit_booking") {
      return counts.profit_booking ?? 0;
    }
    return counts[item.filter_id] ?? 0;
  }
  return 0;
}

/** Remove leading colon artifacts from API narrative fields. */
export function strip_narrative_leading_colon(text) {
  return String(text || "")
    .replace(/^\s*:\s*/, "")
    .trim();
}

/** Split narrative on semicolons outside parentheses. */
export function split_narrative_clauses(text) {
  const clean = strip_narrative_leading_colon(text);
  if (!clean) return [];

  const parts = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < clean.length; i += 1) {
    const ch = clean[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    else if (ch === ";" && depth === 0) {
      const slice = clean.slice(start, i).trim();
      if (slice) parts.push(slice);
      start = i + 1;
    }
  }
  const tail = clean.slice(start).trim();
  if (tail) parts.push(tail);
  return parts.length ? parts : [clean];
}

/** Optional section lead before first colon (e.g. "Real estate pipeline: …"). */
export function parse_outlook_narrative(text) {
  const clean = strip_narrative_leading_colon(text);
  if (!clean) return { lead: null, bullets: [] };

  const lead_match = clean.match(/^([^:()]{3,56}):\s+(.+)$/s);
  if (lead_match) {
    const bullets = split_narrative_clauses(lead_match[2]);
    return { lead: lead_match[1].trim(), bullets };
  }

  const bullets = split_narrative_clauses(clean);
  return { lead: null, bullets };
}

/** Trailing parenthetical shown as a subdued note under the bullet. */
export function split_narrative_parenthetical(clause) {
  const clean = String(clause || "").trim();
  if (!clean) return { main: "", note: null };
  const match = clean.match(/^(.+?)\s*(\([^)]+\))\s*$/);
  if (match && match[1].trim().length >= 8) {
    return { main: match[1].trim(), note: match[2].trim() };
  }
  return { main: clean, note: null };
}

/** Highlight INR, capacity, dates, and other metrics inside outlook narratives. */
export function split_narrative_highlight_parts(text) {
  if (!text) return [];
  const re =
    /(~?\s*(?:INR|Rs\.?)\s*[\d,]+(?:\.\d+)?(?:\s*(?:crore|cr|lakh|lakhs|billion|million))?|~?[\d,]+(?:\.\d+)?\s*(?:million|crore|lakh|lakhs|billion)\s+(?:sq\s*ft|sqft)|~?[\d,]+(?:\.\d+)?\s*(?:MW|GW|GWh|mtpa|mt)\b|~?[\d,]+(?:\.\d+)?\s*(?:acres?|sq\s*ft|sqft)\b|Q[1-4]\s*FY['']?\d{2,4}|(?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2}(?:\s*[–-]\s*\d{1,2})?|\bDecember\s+\d{4}\b|~?\d+(?:\.\d+)?(?:\s*[-–]\s*\d+(?:\.\d+)?)?%)/gi;

  const parts = [];
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ type: "text", value: text.slice(last, match.index) });
    }
    parts.push({ type: "metric", value: match[0] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push({ type: "text", value: text.slice(last) });
  }
  return parts;
}

/** Split portfolio impact summary into plain text and numeric highlight segments. */
export function split_impact_summary_parts(text) {
  if (!text) return [];
  const re = /(~?\d+(?:\.\d+)?%|\b\d+\.\d+\b)/g;
  const parts = [];
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ type: "text", value: text.slice(last, match.index) });
    }
    parts.push({ type: "num", value: match[0] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push({ type: "text", value: text.slice(last) });
  }
  return parts;
}

export function plan_loss_amount(data) {
  const tpp = data?.three_phase_portfolio;
  const footer = tpp?.footer || {};
  const v =
    footer.estimated_residual_unrealized_loss ??
    footer.cumulative_loss_booked_all_phases ??
    data?.action_summary?.net_loss_booked;
  return v;
}
